import { useEffect, useState } from 'react';
import HitlService, { HitlQueueItem, formatDaysAgo, stageReason } from './HitlService';

/**
 * HITL review detail + approve/reject (spec §8.2). Shows alumni context, the proposed change, and
 * the LLM/rule reasoning. Reason/notes enforced per the item's config (stage reviews); optional for
 * pool reviews. Backend re-validates regardless.
 */
const HitlReviewDetailsModal = ({ item, onClose, onDone }: { item: HitlQueueItem; onClose: () => void; onDone: () => void }) => {
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reasonRequired = !!item.config?.reason_required;
    const isPool = item.review_type === 'pool_classification';
    const isPending = item.status === 'pending';

    // For an already-actioned review, load the decision it was closed with so the modal shows the
    // recorded reason/notes (read-only) instead of empty inputs, plus who/when for context.
    const [decision, setDecision] = useState<{ action: string; performed_at: string | null } | null>(null);
    useEffect(() => {
        if (isPending) return;
        let active = true;
        HitlService.getQueueHistory(item.queue_id)
            .then((res: any) => {
                if (!active) return;
                const hist: any[] = res?.history || [];
                const h = hist.find((x) => ['approve', 'reject', 'expire'].includes(x.action)) || hist[0];
                if (h) {
                    setReason(h.reason || '');
                    setNotes(h.notes || '');
                    setDecision({ action: h.action, performed_at: h.performed_at });
                }
            })
            .catch(() => {});
        return () => { active = false; };
    }, [item.queue_id, isPending]);

    const act = async (kind: 'approve' | 'reject') => {
        if (kind === 'approve' && reasonRequired && !reason.trim()) { setError('A reason is required to approve this review.'); return; }
        setBusy(true);
        setError(null);
        try {
            const body = { reason: reason.trim() || undefined, notes: notes.trim() || undefined };
            await (kind === 'approve' ? HitlService.approve(item.queue_id, body) : HitlService.reject(item.queue_id, body));
            onDone();
        } catch (e: any) {
            setError(e?.response?.data?.error?.message || e?.message || 'Action failed.');
            setBusy(false);
        }
    };

    return (
        <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 shadow-lg rounded-4">
                    <div className="modal-header px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
                        <h5 className="modal-title font-16 font-600 text-dark-blue">
                            {isPool ? 'Pool Classification Review' : 'Stage Transition Review'} — {item.name ?? '—'}
                        </h5>
                        <button type="button" className="btn-close shadow-none" onClick={onClose}></button>
                    </div>
                    <div className="modal-body p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {error && <div className="alert alert-danger font-12">{error}</div>}

                        {!isPending && (
                            <div className="alert alert-secondary font-12 py-2 d-flex align-items-center gap-2 mb-3">
                                <span className="badge bg-secondary text-uppercase">{item.status}</span>
                                <span className="text-muted">
                                    This review has already been actioned{decision?.performed_at ? ` on ${String(decision.performed_at).slice(0, 10)}` : ''}.
                                </span>
                            </div>
                        )}

                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="font-11 text-muted text-uppercase">Alumni</div>
                                <div className="font-14 font-600 text-dark-blue">{item.name ?? '—'}</div>
                                <div className="font-12 text-secondary">{item.program_and_year}{item.campus_name ? ` · ${item.campus_name}` : ''}</div>
                            </div>
                            <div className="col-md-6">
                                <div className="font-11 text-muted text-uppercase">Proposed change</div>
                                {isPool ? (
                                    <div className="font-14"><span className="font-600">{item.pool_label}</span>: {item.current_value} → <span className="font-600 text-primary">{item.proposed_value}</span></div>
                                ) : (
                                    <div className="font-14"><span className="font-600">{item.outcome_label}</span>: {item.current_stage_label || '—'} → <span className="font-600 text-primary">{item.proposed_stage_label}</span></div>
                                )}
                                <div className="font-11 text-muted mt-1">Proposed {formatDaysAgo(item.days_pending)}{item.expires_at ? ` · expires ${String(item.expires_at).slice(0, 10)}` : ''}</div>
                            </div>
                        </div>

                        <div className="d-flex gap-3 mb-3">
                            <span className="badge bg-primary bg-opacity-10 text-primary font-11">Ability {item.overall_ability ?? '—'}</span>
                            <span className="badge bg-success bg-opacity-10 text-success font-11">Willingness {item.overall_willingness ?? '—'}</span>
                            <span className="badge bg-warning bg-opacity-10 text-warning font-11">Involvement {item.overall_involvement ?? '—'}</span>
                        </div>

                        {isPool && item.ambiguity_flag && (
                            <div className="alert alert-warning font-12 py-2">🚩 {item.ambiguity_flag.replace(/_/g, ' ')}{item.ambiguity_reason ? ` — ${item.ambiguity_reason}` : ''}</div>
                        )}
                        {(isPool ? item.rule_trace : stageReason(item.proposed_stage) || item.rule_trace) && (
                            <div className="mb-3">
                                <div className="font-11 text-muted text-uppercase mb-1">{isPool ? 'LLM reasoning' : 'Why this transition'}</div>
                                <div className="font-12 text-secondary bg-light rounded-3 p-2" title={!isPool ? item.rule_trace || undefined : undefined}>
                                    {isPool ? item.rule_trace : stageReason(item.proposed_stage) || item.rule_trace}
                                </div>
                            </div>
                        )}

                        <div className="mb-2">
                            <label className="form-label text-muted font-12 font-600">Reason {isPending && reasonRequired && <span className="text-danger">*</span>}</label>
                            <textarea className="form-control rounded-3 h-auto font-14" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={isPending ? 'Why?' : '—'} readOnly={!isPending} />
                        </div>
                        <div>
                            <label className="form-label text-muted font-12 font-600">Notes {isPending && <span className="text-muted font-11">(optional)</span>}</label>
                            <textarea className="form-control rounded-3 h-auto font-14" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={isPending ? 'Optional context' : '—'} readOnly={!isPending} />
                        </div>
                    </div>
                    <div className="modal-footer px-4 py-3 border-top d-flex justify-content-end gap-2">
                        {isPending ? (
                            <>
                                <button className="btn btn-outline-secondary px-4 rounded-3" onClick={onClose} disabled={busy}>Cancel</button>
                                <button className="btn btn-outline-danger px-4 rounded-3" onClick={() => act('reject')} disabled={busy}>{busy ? '…' : 'Reject'}</button>
                                <button className="btn btn-primary px-4 rounded-3" onClick={() => act('approve')} disabled={busy}>{busy ? '…' : 'Approve'}</button>
                            </>
                        ) : (
                            <button className="btn btn-outline-secondary px-4 rounded-3" onClick={onClose}>Close</button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HitlReviewDetailsModal;
